import { useErrorHandlingQuery } from '@backoffice-frontend/data-fetching'
import {
  MapControls,
  ODD_TOGGLE_TYPES,
} from '@backoffice-frontend/overwatch-map-common'
import {
  Breadcrumb,
  MoiaHighTopBar,
  MoiaTopBarTitle,
  MoiaViewBox,
} from '@backoffice-frontend/patterns'
import { PlusIcon, RouteIcon } from '@moia-dev/pace-icons'
import { Button, LoadingIndicator, Option, Select } from '@moia-dev/pace-web'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
  useParams,
} from 'react-router'
import { RouteBuilderAreaRoutes } from '../RouteBuilderAreaRoutes'
import { useRouteBuilderMetropolisesQuery } from './SavedTrips/SavedTrips.hook'
import { SavedTripsHeaderTabs } from './SavedTrips/SavedTripsHeaderTabs'
import { SavedTripsTabsProvider } from './SavedTrips/SavedTripsTabsContext'
import { SavedTripsView } from './SavedTrips/SavedTripsView'
import { SharedMapProvider, useSharedMapContext } from './SharedMapContext'
import { CreateFromVtrView } from './TripEditor/CreateFromVtrView'
import { EditTripView } from './TripEditor/EditTripView'
import { MapOverlayOptions } from './TripEditor/MapOverlayOptions'
import { ROUTE_LAYER_ID, RouteMap } from './TripEditor/RouteMap'
import { useRouteBuilderSavedRouteDetailsQuery } from './TripEditor/TripEditor.hook'
import type { EditingTrip } from './TripEditor/TripEditorView'
import { TripEditorView } from './TripEditor/TripEditorView'
import type { AssigningVehicle } from './TripEditor/types'
import {
  computeMapPadding,
  PANEL_GAP,
  PANEL_WIDTH,
  SIDEBAR_WIDTH,
} from './utils'

const DEFAULT_CENTER: [number, number] = [9.993, 53.551]

// ODD types that have a sidebar toggle. RouteMap's route-ODD markers are gated by
// the toggle state for these types; element types without a toggle are always
// shown (they can't be turned off, so gating them would hide them permanently).
const TOGGLEABLE_ODD_TYPES = new Set(ODD_TOGGLE_TYPES.map(({ type }) => type))

/**
 * Wrapper for the create route — picks up duplicateFrom navigation state.
 */
const CreateTripRoute = ({
  metropolisId,
  metropolisName,
  mapCenter,
  assigningVehicle,
}: {
  metropolisId: number
  metropolisName?: string
  mapCenter: [number, number]
  assigningVehicle?: AssigningVehicle
}) => {
  const location = useLocation()
  // biome-ignore lint/suspicious/noExplicitAny: navigation state is untyped
  const navState = (location.state ?? {}) as any
  const duplicateFrom = navState.duplicateFrom as EditingTrip | undefined
  const duplicateFromTripId = navState.duplicateFromTripId as string | undefined

  return (
    <TripEditorView
      key={
        duplicateFrom
          ? `dup-${duplicateFromTripId ?? duplicateFrom.name}`
          : 'create'
      }
      metropolisId={metropolisId}
      metropolisName={metropolisName}
      mapCenter={mapCenter}
      editingTrip={duplicateFrom ? { ...duplicateFrom, id: '' } : undefined}
      assigningVehicle={assigningVehicle}
      headerTitle={assigningVehicle ? 'Building Route' : undefined}
    />
  )
}

/**
 * Wrapper for the assign trip route — reads VIN from URL params.
 * Vehicle name and location come from navigation state (passed by Vehicle Monitoring).
 * Falls back to defaults for direct URL access / testing.
 */
const AssignTripRoute = ({
  metropolisId,
  metropolisName,
  mapCenter,
}: {
  metropolisId: number
  metropolisName?: string
  mapCenter: [number, number]
}) => {
  const { vin } = useParams<{ vin: string }>()
  // biome-ignore lint/suspicious/noExplicitAny: navigation state is untyped
  const navState = (window.history.state?.usr ?? {}) as any
  const vehicle: AssigningVehicle = {
    vin: vin ?? 'unknown',
    name: navState.vehicleName ?? vin ?? 'Vehicle',
    location: navState.vehicleLocation ?? {
      latitude: mapCenter[1],
      longitude: mapCenter[0],
    },
  }
  return (
    <TripEditorView
      metropolisId={metropolisId}
      metropolisName={metropolisName}
      mapCenter={mapCenter}
      assigningVehicle={vehicle}
    />
  )
}

const EDITOR_PATHS = new Set(['create', 'create-from-vtr', 'edit', 'assign'])

const isEditorPath = (pathname: string) => {
  const segments = pathname.split('/').filter(Boolean)
  return segments.some(s => EDITOR_PATHS.has(s))
}

/**
 * Inner layout: renders the persistent shared RouteMap with an animated sidebar.
 * - saved-trips: sidebar visible, map fills remaining space
 * - editor routes (create/edit/assign): sidebar slides out, floating panel fades in over map
 * RouteMap never unmounts.
 */
export const RouteBuilderLayout = ({
  metropolisId,
  metropolisName,
  mapCenter,
  assigningVehicle,
}: {
  metropolisId: number
  metropolisName?: string
  mapCenter: [number, number]
  assigningVehicle?: AssigningVehicle
}) => {
  const {
    mapData,
    setMapData,
    callbacksRef,
    onMapReady,
    mapOverlay,
    detailsPanel,
    detailsPanelOpen,
    setDetailsPanelOpen,
    mapInstance,
    overlayOptions,
  } = useSharedMapContext()
  const { t } = useTranslation()
  const location = useLocation()
  const inEditor = isEditorPath(location.pathname)
  // Only the edit route is "editing"; create / create-from-vtr / assign all build
  // a new route. Drives the breadcrumb's current-page label.
  const isEditingRoute = location.pathname.includes('/edit/')
  // Positioned wrapper the expand button drives into fullscreen.
  const mapColumnRef = useRef<HTMLDivElement>(null)
  const [hasActiveLayers, setHasActiveLayers] = useState(false)

  // Stabilize ODD element arrays so RouteMap and MapOverlayOptions don't re-run
  // their imperative effects on view transitions where the content is identical but
  // the array reference is new (saved-trips → editor handoff writes a new array to
  // mapData and overlayOptions even though the elements are the same coordinates).
  const stableMapOddElementsRef = useRef(mapData.oddElements)
  const prevMapOddFingerprintRef = useRef('')
  const mapOddFingerprint = mapData.oddElements
    .map(
      e =>
        `${e.type}:${e.location.latitude.toFixed(6)},${e.location.longitude.toFixed(6)}`,
    )
    .sort()
    .join('|')
  if (mapOddFingerprint !== prevMapOddFingerprintRef.current) {
    prevMapOddFingerprintRef.current = mapOddFingerprint
    stableMapOddElementsRef.current = mapData.oddElements
  }
  const stableMapOddElements = stableMapOddElementsRef.current

  // RouteMap draws its own full-opacity markers for route ODDs. Gate them by the
  // ODD types the sidebar currently has enabled so toggling a type off — or the
  // route dropping it — hides the markers too (they'd otherwise persist even
  // though the sidebar-controlled overlay layer is gone).
  const [activeOddTypes, setActiveOddTypes] = useState<string[]>([])
  const activeOddTypesKey = activeOddTypes.join(',')
  const visibleMapOddElements = useMemo(() => {
    const active = new Set(
      activeOddTypesKey ? activeOddTypesKey.split(',') : [],
    )
    return stableMapOddElements.filter(
      el => !TOGGLEABLE_ODD_TYPES.has(el.type) || active.has(el.type),
    )
  }, [stableMapOddElements, activeOddTypesKey])

  const rawRouteOddElements = overlayOptions?.routeOddElements
  const stableRouteOddElementsRef = useRef(rawRouteOddElements)
  const prevRouteOddFingerprintRef = useRef('')
  const routeOddFingerprint =
    rawRouteOddElements
      ?.map(
        e =>
          `${e.type}:${e.location?.latitude?.toFixed(6)},${e.location?.longitude?.toFixed(6)}`,
      )
      .sort()
      .join('|') ?? ''
  if (routeOddFingerprint !== prevRouteOddFingerprintRef.current) {
    prevRouteOddFingerprintRef.current = routeOddFingerprint
    stableRouteOddElementsRef.current = rawRouteOddElements
  }
  const stableRouteOddElements = stableRouteOddElementsRef.current

  // Two-phase animation: track whether the panel is mounted and whether it's visible.
  // On enter: mount immediately, then set visible on next frame (CSS transition kicks in).
  // On exit: clear visible first, unmount after transition completes.
  const [panelMounted, setPanelMounted] = useState(inEditor)
  const [panelVisible, setPanelVisible] = useState(inEditor)
  const unmountTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const [detailsPanelMounted, setDetailsPanelMounted] =
    useState(detailsPanelOpen)
  const [detailsPanelVisible, setDetailsPanelVisible] =
    useState(detailsPanelOpen)
  const detailsUnmountTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  )

  const TRANSITION_MS = 300

  // Vertical space reserved above the cards for the editor breadcrumb (its ~24px
  // line height plus a PANEL_GAP below it). Only present in editor mode, matching
  // the create-template flow where a breadcrumb sits on the page background above
  // the form + map cards.
  const BREADCRUMB_ROW = 24 + PANEL_GAP
  const cardsTop = inEditor ? PANEL_GAP + BREADCRUMB_ROW : PANEL_GAP

  // Floating-card layout (matches the create-template flow): the sidebar, the
  // details panel, the editor form, and the map are separate rounded cards sitting
  // on the page background, each inset by PANEL_GAP so the background shows through
  // the gaps. The map's left edge sits right of whichever card is showing and
  // slides in step with it. In editor mode the sidebar slides away and the editor
  // form card takes its place *beside* the map (not overlaid), mirroring hulk's
  // create-template layout.
  const sidebarRight = PANEL_GAP + SIDEBAR_WIDTH
  // Left edge of the editor form card + the gap after it — where the map starts in
  // editor mode.
  const editorCardRight = PANEL_GAP + PANEL_WIDTH
  // Drive the map's left edge off `detailsPanelVisible`, not `detailsPanelOpen`.
  // On open, visible flips one requestAnimationFrame AFTER the card mounts, so the
  // heavy details render commits and paints on its own frame first, then the map's
  // width transition and the card's fade start together on the next frame — the
  // mount cost no longer drops the opening animation's first frames (which made
  // open stutter while close, with no mount, stayed smooth). On close, visible
  // flips immediately in the same commit as open→false, so the map begins
  // reclaiming width in step with the fade-out, exactly as before.
  const mapColumnLeft = inEditor
    ? editorCardRight + PANEL_GAP
    : detailsPanelVisible
      ? sidebarRight + PANEL_GAP + PANEL_WIDTH + PANEL_GAP
      : sidebarRight + PANEL_GAP

  useEffect(() => {
    if (unmountTimerRef.current) clearTimeout(unmountTimerRef.current)
    if (inEditor) {
      setPanelMounted(true)
      // Next frame so the browser has painted the initial (invisible) state
      requestAnimationFrame(() => setPanelVisible(true))
    } else {
      setPanelVisible(false)
      unmountTimerRef.current = setTimeout(
        () => setPanelMounted(false),
        TRANSITION_MS,
      )
    }
    return () => {
      if (unmountTimerRef.current) clearTimeout(unmountTimerRef.current)
    }
  }, [inEditor])

  useEffect(() => {
    if (inEditor) setDetailsPanelOpen(false)
  }, [inEditor, setDetailsPanelOpen])

  useEffect(() => {
    if (detailsUnmountTimerRef.current)
      clearTimeout(detailsUnmountTimerRef.current)
    if (detailsPanelOpen) {
      setDetailsPanelMounted(true)
      requestAnimationFrame(() => setDetailsPanelVisible(true))
    } else {
      setDetailsPanelVisible(false)
      detailsUnmountTimerRef.current = setTimeout(
        () => setDetailsPanelMounted(false),
        TRANSITION_MS,
      )
    }
    return () => {
      if (detailsUnmountTimerRef.current)
        clearTimeout(detailsUnmountTimerRef.current)
    }
  }, [detailsPanelOpen])

  // Keep map padding in sync with sidebar/panel layout state.
  // Skip when inEditor — TripEditorView sets its own padding atomically with
  // fitWaypoints so the fit always sees the correct insets.
  useEffect(() => {
    if (inEditor) return
    setMapData({
      padding: computeMapPadding(false),
    })
  }, [inEditor, setMapData])

  return (
    <div css={{ position: 'relative', height: '100%', overflow: 'hidden' }}>
      {/* Editor breadcrumb — sits on the page background above the cards, exactly
          like the create-template flow. Mounted with the editor panel and fades in
          with it. Links back to the saved-routes list; the current page reflects
          whether we're building or editing. */}
      {panelMounted && (
        <div
          css={theme => ({
            position: 'absolute',
            top: PANEL_GAP,
            left: PANEL_GAP,
            right: PANEL_GAP,
            height: 24,
            display: 'flex',
            alignItems: 'center',
            zIndex: 6,
            opacity: panelVisible ? 1 : 0,
            transition: `opacity ${TRANSITION_MS}ms ease`,
            paddingLeft: theme.space.Space1,
          })}
        >
          <Breadcrumb
            breadcrumbs={[
              {
                label: t('routeBuilder_breadcrumb_savedRoutes'),
                to: RouteBuilderAreaRoutes.SavedTrips.route,
              },
              {
                label: isEditingRoute
                  ? t('routeBuilder_breadcrumb_editingRoute')
                  : t('routeBuilder_breadcrumb_buildingRoute'),
                to: location.pathname,
              },
            ]}
          />
        </div>
      )}

      {/* Sliding sidebar — absolutely positioned so it never affects the map column's width.
          Slides out via transform so the map stays full-width throughout the animation. */}
      <div
        css={theme => ({
          position: 'absolute',
          top: cardsTop,
          left: PANEL_GAP,
          bottom: PANEL_GAP,
          width: SIDEBAR_WIDTH,
          borderRadius: theme.radius.RadiusLarge,
          border: `1px solid ${theme.semantic.ColorAccessoryReduced}`,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          zIndex: 5,
          backgroundColor: theme.semantic.ColorSurfaceDefault,
          transition: `transform ${TRANSITION_MS}ms ease, top ${TRANSITION_MS}ms ease`,
          transform: inEditor
            ? `translateX(-${SIDEBAR_WIDTH + PANEL_GAP}px)`
            : 'translateX(0)',
        })}
      >
        <Routes>
          <Route index element={<Navigate to="saved-trips" replace />} />
          <Route
            path="saved-trips"
            element={
              <SavedTripsView
                metropolisId={metropolisId}
                mapCenter={mapCenter}
                assigningVehicle={assigningVehicle}
              />
            }
          />
          {/* Editor routes still need to render here so React Router matches,
              but their visual output is shown in the floating panel below */}
          <Route path="create" element={null} />
          <Route path="create-from-vtr" element={null} />
          <Route path="edit/:id" element={null} />
          <Route path="assign/:vin" element={null} />
        </Routes>
      </div>

      {/* Persistent map column — sits beside whichever left card is showing (the
          sidebar on saved-trips, the editor form card in editor mode), like the
          create-template flow, so the map never spans under it. The left edge
          animates in step with the card swap. MapLibre's ResizeObserver resizes
          the canvas. */}
      <div
        ref={mapColumnRef}
        css={theme => ({
          position: 'absolute',
          top: cardsTop,
          right: PANEL_GAP,
          bottom: PANEL_GAP,
          left: mapColumnLeft,
          borderRadius: theme.radius.RadiusLarge,
          border: `1px solid ${theme.semantic.ColorAccessoryReduced}`,
          overflow: 'hidden',
          // Smoothly animate the shrink/expand as the details card fades in/out
          // beneath (zIndex 10 keeps the map above the card). The route-fit in
          // RouteMap waits for this width transition to settle before framing, so
          // the fit reads the FINAL canvas width and the route lands centred in a
          // single camera move rather than tracking a mid-transition width.
          zIndex: 10,
          transition: `left ${TRANSITION_MS}ms ease, top ${TRANSITION_MS}ms ease`,
        })}
      >
        <RouteMap
          center={mapData.center}
          mode={mapData.mode}
          waypoints={mapData.waypoints}
          routeCoordinates={mapData.routeCoordinates}
          violations={mapData.violations}
          sdsViolations={mapData.sdsViolations}
          fitWaypoints={mapData.fitWaypoints}
          fitKey={mapData.fitKey}
          oddElements={visibleMapOddElements}
          vehicleMarker={mapData.vehicleMarker}
          headings={mapData.headings}
          pendingHeadingWaypointId={mapData.pendingHeadingWaypointId}
          onMapClick={(...args) => callbacksRef.current.onMapClick?.(...args)}
          onMarkerClick={(...args) =>
            callbacksRef.current.onMarkerClick?.(...args)
          }
          onMarkerDrag={(...args) =>
            callbacksRef.current.onMarkerDrag?.(...args)
          }
          onMarkerDragStart={(...args) =>
            callbacksRef.current.onMarkerDragStart?.(...args)
          }
          onOddElementClick={(...args) =>
            callbacksRef.current.onOddElementClick?.(...args)
          }
          onMapReady={onMapReady}
          onHeadingSelect={(...args) =>
            callbacksRef.current.onHeadingSelect?.(...args)
          }
          onRemoveHeading={(...args) =>
            callbacksRef.current.onRemoveHeading?.(...args)
          }
          padding={mapData.padding}
        />

        {/* Overlays from editor (toasts, loading) */}
        {mapOverlay}

        {/* Shared control rail — left side. MapOverlayOptions stays mounted
            (via the panel slot) so its layer state survives navigation; only the
            layers button is gated to when a view registered options. */}
        <MapControls
          map={mapInstance}
          fullscreenTargetRef={mapColumnRef}
          showLayersButton={Boolean(overlayOptions) || inEditor}
          hasActiveLayers={hasActiveLayers}
          layersPanel={
            <MapOverlayOptions
              map={mapInstance}
              metropolisId={overlayOptions?.metropolisId ?? metropolisId}
              metropolisName={overlayOptions?.metropolisName ?? metropolisName}
              autoEnableLanes={overlayOptions?.autoEnableLanes ?? false}
              autoEnableGeonets={overlayOptions?.autoEnableGeonets ?? false}
              onOddElementClick={overlayOptions?.onOddElementClick}
              routeOddElements={stableRouteOddElements}
              vtrRequiredTypes={overlayOptions?.vtrRequiredTypes}
              insertBeforeLayer={ROUTE_LAYER_ID}
              onActiveLayersChange={setHasActiveLayers}
              onActiveOddTypesChange={setActiveOddTypes}
            />
          }
        />
      </div>

      {/* Editor form card — sits BESIDE the map (in the space the sidebar vacates),
          mirroring the create-template flow. Fades + slides in from the left; the
          map's left edge animates in step so the two never overlap. */}
      {panelMounted && (
        <div
          css={theme => ({
            position: 'absolute',
            top: cardsTop,
            left: PANEL_GAP,
            bottom: PANEL_GAP,
            width: PANEL_WIDTH,
            backgroundColor: theme.semantic.ColorSurfaceDefault,
            borderRadius: theme.radius.RadiusLarge,
            border: `1px solid ${theme.semantic.ColorAccessoryReduced}`,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            zIndex: 20,
            // Animate: fade + slide from left
            opacity: panelVisible ? 1 : 0,
            transform: panelVisible ? 'translateX(0)' : 'translateX(-24px)',
            transition: `opacity ${TRANSITION_MS}ms ease, transform ${TRANSITION_MS}ms ease, top ${TRANSITION_MS}ms ease`,
          })}
        >
          <Routes>
            <Route path="saved-trips" element={null} />
            <Route
              path="create"
              element={
                <CreateTripRoute
                  metropolisId={metropolisId}
                  metropolisName={metropolisName}
                  mapCenter={mapCenter}
                  assigningVehicle={assigningVehicle}
                />
              }
            />
            <Route
              path="create-from-vtr"
              element={
                <CreateFromVtrView
                  metropolisId={metropolisId}
                  metropolisName={metropolisName}
                  mapCenter={mapCenter}
                />
              }
            />
            <Route
              path="edit/:id"
              element={
                <EditTripView
                  metropolisId={metropolisId}
                  metropolisName={metropolisName}
                  mapCenter={mapCenter}
                />
              }
            />
            <Route
              path="assign/:vin"
              element={
                <AssignTripRoute
                  metropolisId={metropolisId}
                  metropolisName={metropolisName}
                  mapCenter={mapCenter}
                />
              }
            />
          </Routes>
        </div>
      )}

      {/* Details panel — a floating card the same height as the sidebar and map,
          sitting in the gap to their right. It renders beneath the map (lower
          z-index) and only fades in/out; the map snaps to its offset without
          sliding, so the details card appears/disappears under the map edge. */}
      {!inEditor && detailsPanelMounted && (
        <div
          css={theme => ({
            position: 'absolute',
            top: PANEL_GAP,
            left: sidebarRight + PANEL_GAP,
            bottom: PANEL_GAP,
            width: PANEL_WIDTH,
            backgroundColor: theme.semantic.ColorSurfaceDefault,
            borderRadius: theme.radius.RadiusLarge,
            border: `1px solid ${theme.semantic.ColorAccessoryReduced}`,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            zIndex: 1,
            opacity: detailsPanelVisible ? 1 : 0,
            transition: `opacity ${TRANSITION_MS}ms ease`,
          })}
        >
          {detailsPanel}
        </div>
      )}
    </div>
  )
}

type ModalView =
  | { type: 'saved-trips' }
  | {
      type: 'create'
      duplicateFrom?: EditingTrip
      duplicateFromTripId?: string
    }
  | { type: 'edit'; tripId: string }

/**
 * Loads saved route details and renders TripEditorView for edit mode in the modal.
 */
const ModalEditView = ({
  tripId,
  metropolisId,
  metropolisName,
  mapCenter,
  assigningVehicle,
  onBack,
  onDismiss,
}: {
  tripId: string
  metropolisId: number
  metropolisName?: string
  mapCenter: [number, number]
  assigningVehicle?: AssigningVehicle
  onBack: () => void
  onDismiss: () => void
}) => {
  const { mapData } = useSharedMapContext()
  const { data, loading } = useErrorHandlingQuery(
    useRouteBuilderSavedRouteDetailsQuery,
    { variables: { id: tripId } },
  )
  const savedRouteDetails = data?.savedRouteDetails
  const savedTrip = savedRouteDetails?.trip

  if (loading || !savedRouteDetails || !savedTrip) {
    return (
      <div
        css={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
        }}
      >
        <LoadingIndicator size={24} />
      </div>
    )
  }

  const segments = savedTrip.segments.flatMap(({ segment }) => {
    const desiredLocation =
      segment?.stop?.desiredLocation ?? segment?.destination
    if (!segment || !desiredLocation) return []
    return [{ segment, desiredLocation }]
  })

  const editingTrip: EditingTrip = {
    id: savedRouteDetails.id,
    tripId: savedTrip.id,
    name: savedRouteDetails.name,
    description: savedRouteDetails.description,
    tripType: savedTrip.type,
    segments: segments.map(({ segment: seg, desiredLocation }) => ({
      id: seg.id,
      sequenceId: seg.sequenceId,
      metadata: seg.metadata,
      destination: {
        desiredLocation: {
          ...desiredLocation,
          heading: seg.stop?.desiredLocation?.heading || null,
        },
        snappedLocation: seg.stop?.snappedLocation,
        address: seg.stop?.desiredAddress,
        stopType: seg.stop?.destinationBehavior?.stopType,
        oddElementType: seg.stop?.oddElementType,
      },
      numPassengers: seg.numPassengers,
      route: seg.route,
    })),
    codebeamerId: savedRouteDetails.assignedVtrs[0]?.codebeamerIds[0],
    associatedVtr: savedRouteDetails.assignedVtrs[0]?.vtr,
    assignedVtrs: savedRouteDetails.assignedVtrs,
  }

  return (
    <TripEditorView
      key={savedRouteDetails.id}
      metropolisId={metropolisId}
      metropolisName={metropolisName}
      mapCenter={mapCenter}
      editingTrip={editingTrip}
      initialOddElements={mapData.oddElements}
      assigningVehicle={assigningVehicle}
      headerTitle="Building Route"
      onBack={onBack}
      onDismiss={onDismiss}
    />
  )
}

/**
 * State-driven version of RouteBuilderLayout for embedding in a modal.
 * Uses view state + callbacks instead of React Router, so it can live inside
 * a portal without needing a nested <MemoryRouter>.
 *
 * Must be rendered inside a <SharedMapProvider>.
 */
export const RouteBuilderModalLayout = ({
  metropolisId,
  metropolisName,
  mapCenter,
  assigningVehicle,
  initialView = 'saved-trips',
  initialEditingTrip,
  onDismiss,
}: {
  metropolisId: number
  metropolisName?: string
  mapCenter: [number, number]
  assigningVehicle?: AssigningVehicle
  /** Which view to open on first render. Defaults to 'saved-trips'. */
  initialView?: 'saved-trips' | 'create'
  /** Pre-populate the create view with an existing trip (e.g. active trip with status data). */
  initialEditingTrip?: EditingTrip | null
  /** Called when the user dismisses the modal, or after successful assignment. */
  onDismiss: () => void
}) => {
  const {
    mapData,
    setMapData,
    callbacksRef,
    onMapReady,
    mapOverlay,
    detailsPanel,
    detailsPanelOpen,
    setDetailsPanelOpen,
    mapInstance,
    overlayOptions,
  } = useSharedMapContext()

  const [view, setView] = useState<ModalView>(
    initialView === 'create' ? { type: 'create' } : { type: 'saved-trips' },
  )
  const inEditor = view.type !== 'saved-trips'
  const mapColumnRef = useRef<HTMLDivElement>(null)
  const [hasActiveLayers, setHasActiveLayers] = useState(false)

  const handleNavigateToCreate = (opts?: {
    duplicateFrom?: EditingTrip
    duplicateFromTripId?: string
  }) => {
    setView({
      type: 'create',
      duplicateFrom: opts?.duplicateFrom,
      duplicateFromTripId: opts?.duplicateFromTripId,
    })
  }

  const handleNavigateToEdit = (tripId: string) => {
    setView({ type: 'edit', tripId })
  }

  const handleBackToList = () => {
    setView({ type: 'saved-trips' })
  }

  // ODD element fingerprinting — same logic as RouteBuilderLayout
  const stableMapOddElementsRef = useRef(mapData.oddElements)
  const prevMapOddFingerprintRef = useRef('')
  const mapOddFingerprint = mapData.oddElements
    .map(
      e =>
        `${e.type}:${e.location.latitude.toFixed(6)},${e.location.longitude.toFixed(6)}`,
    )
    .sort()
    .join('|')
  if (mapOddFingerprint !== prevMapOddFingerprintRef.current) {
    prevMapOddFingerprintRef.current = mapOddFingerprint
    stableMapOddElementsRef.current = mapData.oddElements
  }
  const stableMapOddElements = stableMapOddElementsRef.current

  // Gate RouteMap's route-ODD markers by the sidebar's enabled ODD types — same
  // logic as RouteBuilderLayout.
  const [activeOddTypes, setActiveOddTypes] = useState<string[]>([])
  const activeOddTypesKey = activeOddTypes.join(',')
  const visibleMapOddElements = useMemo(() => {
    const active = new Set(
      activeOddTypesKey ? activeOddTypesKey.split(',') : [],
    )
    return stableMapOddElements.filter(
      el => !TOGGLEABLE_ODD_TYPES.has(el.type) || active.has(el.type),
    )
  }, [stableMapOddElements, activeOddTypesKey])

  const rawRouteOddElements = overlayOptions?.routeOddElements
  const stableRouteOddElementsRef = useRef(rawRouteOddElements)
  const prevRouteOddFingerprintRef = useRef('')
  const routeOddFingerprint =
    rawRouteOddElements
      ?.map(
        e =>
          `${e.type}:${e.location?.latitude?.toFixed(6)},${e.location?.longitude?.toFixed(6)}`,
      )
      .sort()
      .join('|') ?? ''
  if (routeOddFingerprint !== prevRouteOddFingerprintRef.current) {
    prevRouteOddFingerprintRef.current = routeOddFingerprint
    stableRouteOddElementsRef.current = rawRouteOddElements
  }
  const stableRouteOddElements = stableRouteOddElementsRef.current

  // Two-phase panel animation — same logic as RouteBuilderLayout
  const [panelMounted, setPanelMounted] = useState(inEditor)
  const [panelVisible, setPanelVisible] = useState(inEditor)
  const unmountTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const [detailsPanelMounted, setDetailsPanelMounted] =
    useState(detailsPanelOpen)
  const [detailsPanelVisible, setDetailsPanelVisible] =
    useState(detailsPanelOpen)
  const detailsUnmountTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  )

  const TRANSITION_MS = 300

  useEffect(() => {
    if (unmountTimerRef.current) clearTimeout(unmountTimerRef.current)
    if (inEditor) {
      setPanelMounted(true)
      requestAnimationFrame(() => setPanelVisible(true))
    } else {
      setPanelVisible(false)
      unmountTimerRef.current = setTimeout(
        () => setPanelMounted(false),
        TRANSITION_MS,
      )
    }
    return () => {
      if (unmountTimerRef.current) clearTimeout(unmountTimerRef.current)
    }
  }, [inEditor])

  useEffect(() => {
    if (inEditor) setDetailsPanelOpen(false)
  }, [inEditor, setDetailsPanelOpen])

  useEffect(() => {
    if (detailsUnmountTimerRef.current)
      clearTimeout(detailsUnmountTimerRef.current)
    if (detailsPanelOpen) {
      setDetailsPanelMounted(true)
      requestAnimationFrame(() => setDetailsPanelVisible(true))
    } else {
      setDetailsPanelVisible(false)
      detailsUnmountTimerRef.current = setTimeout(
        () => setDetailsPanelMounted(false),
        TRANSITION_MS,
      )
    }
    return () => {
      if (detailsUnmountTimerRef.current)
        clearTimeout(detailsUnmountTimerRef.current)
    }
  }, [detailsPanelOpen])

  useEffect(() => {
    if (inEditor) return
    setMapData({
      // Full-bleed map (spans under the flush sidebar), so keep the fit clear of it.
      padding: computeMapPadding(false, true),
    })
  }, [inEditor, setMapData])

  return (
    <div css={{ position: 'relative', height: '100%', overflow: 'hidden' }}>
      {/* Sliding sidebar — flush to the modal edge, over a full-bleed map. The map
          spans the whole modal (inset:0) and the sidebar slides out via transform,
          so the map fills the modal exactly as it did on main. */}
      <div
        css={theme => ({
          position: 'absolute',
          top: 0,
          left: 0,
          bottom: 0,
          width: SIDEBAR_WIDTH,
          borderRight: `1px solid ${theme.semantic.ColorAccessoryReduced}`,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          zIndex: 5,
          backgroundColor: theme.semantic.ColorBackgroundDefault,
          transition: `transform ${TRANSITION_MS}ms ease`,
          transform: inEditor
            ? `translateX(-${SIDEBAR_WIDTH}px)`
            : 'translateX(0)',
        })}
      >
        <SavedTripsView
          metropolisId={metropolisId}
          mapCenter={mapCenter}
          assigningVehicle={assigningVehicle}
          onNavigateToCreate={handleNavigateToCreate}
          onNavigateToEdit={handleNavigateToEdit}
          inEditorOverride={inEditor}
          onAssignSuccess={onDismiss}
        />
      </div>

      {/* Full-bleed map — fills the entire modal; the sidebar and panels float
          over it. MapLibre's ResizeObserver keeps the canvas sized to the modal. */}
      <div
        ref={mapColumnRef}
        css={{
          position: 'absolute',
          inset: 0,
          overflow: 'hidden',
        }}
      >
        <RouteMap
          center={mapData.center}
          mode={mapData.mode}
          waypoints={mapData.waypoints}
          routeCoordinates={mapData.routeCoordinates}
          violations={mapData.violations}
          sdsViolations={mapData.sdsViolations}
          fitWaypoints={mapData.fitWaypoints}
          fitKey={mapData.fitKey}
          oddElements={visibleMapOddElements}
          vehicleMarker={mapData.vehicleMarker}
          headings={mapData.headings}
          pendingHeadingWaypointId={mapData.pendingHeadingWaypointId}
          onMapClick={(...args) => callbacksRef.current.onMapClick?.(...args)}
          onMarkerClick={(...args) =>
            callbacksRef.current.onMarkerClick?.(...args)
          }
          onMarkerDrag={(...args) =>
            callbacksRef.current.onMarkerDrag?.(...args)
          }
          onMarkerDragStart={(...args) =>
            callbacksRef.current.onMarkerDragStart?.(...args)
          }
          onOddElementClick={(...args) =>
            callbacksRef.current.onOddElementClick?.(...args)
          }
          onMapReady={onMapReady}
          onHeadingSelect={(...args) =>
            callbacksRef.current.onHeadingSelect?.(...args)
          }
          onRemoveHeading={(...args) =>
            callbacksRef.current.onRemoveHeading?.(...args)
          }
          padding={mapData.padding}
        />

        {mapOverlay}

        {/* Shared control rail — right side. The left edge of the full-bleed map
            is permanently occupied by the flush sidebar and the floating create-
            route / editor panel, so the rail hugs the right edge to stay clear of
            the create-route flow. Layers panel stays mounted to preserve state. */}
        <MapControls
          map={mapInstance}
          side="right"
          fullscreenTargetRef={mapColumnRef}
          showLayersButton={Boolean(overlayOptions) || inEditor}
          hasActiveLayers={hasActiveLayers}
          layersPanel={
            <MapOverlayOptions
              map={mapInstance}
              metropolisId={overlayOptions?.metropolisId ?? metropolisId}
              metropolisName={overlayOptions?.metropolisName ?? metropolisName}
              autoEnableLanes={overlayOptions?.autoEnableLanes ?? false}
              autoEnableGeonets={overlayOptions?.autoEnableGeonets ?? false}
              onOddElementClick={overlayOptions?.onOddElementClick}
              routeOddElements={stableRouteOddElements}
              vtrRequiredTypes={overlayOptions?.vtrRequiredTypes}
              onActiveLayersChange={setHasActiveLayers}
              onActiveOddTypesChange={setActiveOddTypes}
            />
          }
        />

        {/* Floating editor panel */}
        {panelMounted && (
          <div
            css={theme => ({
              position: 'absolute',
              top: theme.space.Space4,
              left: theme.space.Space4,
              bottom: theme.space.Space4,
              width: PANEL_WIDTH,
              backgroundColor: theme.semantic.ColorBackgroundDefault,
              borderRadius: 12,
              boxShadow: '0 4px 24px rgba(0,0,0,0.18)',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              zIndex: 200,
              opacity: panelVisible ? 1 : 0,
              transform: panelVisible ? 'translateX(0)' : 'translateX(-24px)',
              transition: `opacity ${TRANSITION_MS}ms ease, transform ${TRANSITION_MS}ms ease`,
            })}
          >
            {view.type === 'create' && (
              <TripEditorView
                key={
                  view.duplicateFrom
                    ? `dup-${view.duplicateFromTripId ?? view.duplicateFrom.name}`
                    : 'create'
                }
                metropolisId={metropolisId}
                metropolisName={metropolisName}
                mapCenter={mapCenter}
                editingTrip={
                  view.duplicateFrom
                    ? { ...view.duplicateFrom, id: '' }
                    : (initialEditingTrip ?? undefined)
                }
                assigningVehicle={assigningVehicle}
                headerTitle="Building Route"
                onBack={handleBackToList}
                onDismiss={onDismiss}
              />
            )}
            {view.type === 'edit' && (
              <ModalEditView
                tripId={view.tripId}
                metropolisId={metropolisId}
                metropolisName={metropolisName}
                mapCenter={mapCenter}
                assigningVehicle={assigningVehicle}
                onBack={handleBackToList}
                onDismiss={onDismiss}
              />
            )}
          </div>
        )}
      </div>

      {/* Details panel — floats over the full-bleed map, flush to the right of the
          sidebar, and fades in/out. */}
      {!inEditor && detailsPanelMounted && (
        <div
          css={theme => ({
            position: 'absolute',
            top: PANEL_GAP,
            left: SIDEBAR_WIDTH + PANEL_GAP,
            bottom: PANEL_GAP,
            width: PANEL_WIDTH,
            backgroundColor: theme.semantic.ColorBackgroundDefault,
            borderRadius: 12,
            boxShadow: '0 4px 24px rgba(0,0,0,0.18)',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            zIndex: 200,
            opacity: detailsPanelVisible ? 1 : 0,
            transition: `opacity ${TRANSITION_MS}ms ease`,
          })}
        >
          {detailsPanel}
        </div>
      )}
    </div>
  )
}

export const RouteBuilderAreaContentView = () => {
  const navigate = useNavigate()
  const location = useLocation()
  // Hide the Build Route action while in the create/edit flow — you're already
  // building a route there.
  const inEditor = isEditorPath(location.pathname)
  const { data: metropolisesData, loading: metropolisesLoading } =
    useErrorHandlingQuery(useRouteBuilderMetropolisesQuery, { variables: {} })
  const metropolises = metropolisesData?.metropolises ?? []

  const [selectedMetropolisId, setSelectedMetropolisId] = useState<
    number | null
  >(null)
  const effectiveMetropolisId =
    selectedMetropolisId ?? (Number(metropolises[0]?.id) || 1)

  const selectedMetropolis = metropolises.find(
    m => Number(m.id) === effectiveMetropolisId,
  )
  const mapCenter: [number, number] = useMemo(
    () =>
      selectedMetropolis?.location
        ? [
            selectedMetropolis.location.longitude,
            selectedMetropolis.location.latitude,
          ]
        : DEFAULT_CENTER,
    [selectedMetropolis],
  )

  return (
    <SavedTripsTabsProvider>
      <title>Route Builder</title>
      {/* Tabbed header — the Saved/Archived tabs render in the dedicated header
          tab row, exactly like the hulk-trips Vehicles/Templates tabs. The
          Metropolis picker sits in the upper header row (trailing), next to the
          account dropdown as on other screens, and the Build Route action sits on
          the right of the tab row, opposite the tabs. */}
      <MoiaHighTopBar
        leading={<MoiaTopBarTitle icon={<RouteIcon />} title="Route Builder" />}
        trailing={
          metropolises.length > 0 ? (
            <Select
              aria-label="Metropolis"
              size="compact"
              value={String(effectiveMetropolisId)}
              onChange={value => {
                if (value) setSelectedMetropolisId(Number(value))
              }}
              renderValue={value =>
                metropolises.find(m => String(m.id) === value)?.name ?? value
              }
              options={metropolises.map(m => (
                <Option key={m.id} value={String(m.id)}>
                  {m.name}
                </Option>
              ))}
              css={{ minWidth: 160 }}
            />
          ) : undefined
        }
        tabs={<SavedTripsHeaderTabs />}
        actions={
          inEditor ? undefined : (
            <Button
              size="compact"
              variant="primary"
              label="Build Route"
              icon={<PlusIcon />}
              onClick={() => navigate(RouteBuilderAreaRoutes.CreateTrip.route)}
            />
          )
        }
      />
      {/* Zero the box padding — the floating cards inset themselves by PANEL_GAP,
          so this keeps a single uniform 16px margin instead of doubling it. */}
      <MoiaViewBox
        paddingTop={0}
        paddingBottom={0}
        paddingLeft={0}
        paddingRight={0}
        // Offset by the tabbed top bar's height so the floating cards fill
        // exactly the space below the header — same mechanism the hulk-trips
        // create flow uses. Use the theme token, not a hardcoded px value.
        css={theme => ({
          height: `calc(100vh - ${theme.paceHeaderHeight.desktop.double}px)`,
        })}
      >
        {/* Transparent container — the sidebar, details, and map float as separate
            rounded cards inside it, so the page background shows through the gaps. */}
        <div
          css={{
            height: '100%',
            overflow: 'hidden',
            position: 'relative',
          }}
        >
          {metropolisesLoading ? (
            <div
              css={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <LoadingIndicator size={32} />
            </div>
          ) : (
            <SharedMapProvider initialCenter={mapCenter}>
              <RouteBuilderLayout
                metropolisId={effectiveMetropolisId}
                metropolisName={selectedMetropolis?.name}
                mapCenter={mapCenter}
              />
            </SharedMapProvider>
          )}
        </div>
      </MoiaViewBox>
    </SavedTripsTabsProvider>
  )
}
